import { ParentSize } from "@visx/responsive";
import { scaleBand, scaleLinear, scaleOrdinal } from "@visx/scale";
import { Group } from "@visx/group";
import { BarGroupHorizontal } from "@visx/shape";
import { AxisLeft } from "@visx/axis";
import { RightRoundedRect } from "./RightRoundedRect.js";
import type { Margin } from "./Margin.js";
import { defaultMargin } from "./Margin.js";
import { defaultStyles, useTooltip, useTooltipInPortal } from "@visx/tooltip";
import type { ReactNode } from "react";
import React, { useCallback, useMemo } from "react";
import { invertScaleBand } from "./invertScaleBand.js";


export interface RollDataPoint {
    roll: number;
    [group: string]: number;
}

interface SelectedBar {
    roll: number;
    group: string;
}


interface RollDistributionVisProps {
    className?: string;
    width: number;
    height: number;
    rollProbs: number[];
    data: RollDataPoint[];
    colours: { [group: string]: string };
    renderTooltip: (bar: SelectedBar) => ReactNode;
    margin?: Margin;
}


function RollDistributionVis({
    className, width, height, rollProbs,
    data, colours, renderTooltip, margin,
}: RollDistributionVisProps) {

    margin = useMemo(() => {
        const m = { ...(margin ?? defaultMargin) };
        m.left += 25; // Add space for axis.
        return m;
    }, [margin]);

    const renderXMax = width - margin.left - margin.right;
    const renderYMax = height - margin.top - margin.bottom;

    const baselineData: [number, number][] = [];
    for (let index = 0; index < rollProbs.length; ++index) {
        const prob = rollProbs[index];
        baselineData.push([index, prob]);
        baselineData.push([index + 1, prob]);
    }

    const keys = Object.keys(data[0]).filter(key => key !== "roll");
    const keyColours = keys.map(key => colours[key]);
    const allValues = data.map(d => keys.map(k => d[k])).flat();

    const rollScale = scaleBand({
        domain: rollProbs.map((_, roll) => roll),
        range: [0, renderYMax],
        paddingInner: 0.3,
    });
    const inverseRollScale = invertScaleBand(rollScale);

    const groupScale = scaleBand({
        domain: keys,
        range: [0, rollScale.bandwidth()],
    });
    const inverseGroupScale = invertScaleBand(groupScale);

    const rollPadding = rollScale.step() - rollScale.bandwidth();

    const probScale = scaleLinear<number>({
        domain: [0, Math.max(...allValues, ...rollProbs)],
        range: [0, renderXMax],
    });
    const colourScale = scaleOrdinal<string, string>({
        domain: keys,
        range: keyColours,
    });

    const { containerRef, containerBounds, TooltipInPortal } = useTooltipInPortal({
        scroll: true,
        detectBounds: true,
        zIndex: 1,
    });

    const {
        showTooltip,
        hideTooltip,
        tooltipOpen,
        tooltipData,
        tooltipLeft = 0,
        tooltipTop = 0,
    } = useTooltip<SelectedBar>();

    const calcRollY = (roll: number) => {
        const rawY = rollScale(roll) ?? renderYMax;
        const dy = (roll > 0 && roll < rollProbs.length ? -rollPadding / 2 : 0);
        return rawY + dy;
    };

    const updateMouse = useCallback((
        clientX: number | undefined,
        clientY: number | undefined,
    ) => {
        if (clientX === undefined || clientY === undefined) {
            hideTooltip();
            return;
        }

        const y = clientY - containerBounds.top - margin.top;
        if (y < 0 || y > height - margin.top - margin.bottom) {
            hideTooltip();
            return;
        }

        const roll = inverseRollScale(y);
        if (roll === undefined) {
            hideTooltip();
            return;
        }

        const group = inverseGroupScale(y % rollScale.step());
        if (group === undefined) {
            hideTooltip();
            return;
        }

        showTooltip({
            tooltipLeft: clientX - containerBounds.left,
            tooltipTop: clientY - containerBounds.top,
            tooltipData: { roll, group },
        });
    }, [
        containerBounds, showTooltip, hideTooltip, margin, height,
        inverseRollScale, inverseGroupScale, rollScale,
    ]);

    const onMouseMove = useCallback((event: React.MouseEvent<SVGSVGElement>) => {
        updateMouse(event.clientX, event.clientY);
    }, [updateMouse]);

    const onMouseLeave = useCallback(() => {
        updateMouse(undefined, undefined);
    }, [updateMouse]);

    if (width < 10)
        return null;

    return (
        <>
            {tooltipOpen && tooltipData && (
                <TooltipInPortal
                    className="z-999 bg-white"
                    top={tooltipTop}
                    left={tooltipLeft}
                    style={defaultStyles}>

                    {renderTooltip(tooltipData)}
                </TooltipInPortal>
            )}

            <svg
                className={className}
                ref={containerRef}
                width={width}
                height={height}
                onMouseMove={onMouseMove}
                onMouseEnter={onMouseMove}
                onMouseLeave={onMouseLeave}
                style={{ position: "relative", zIndex: "0" }}>

                <rect x="0" y="0" width={width} height={height} className="fill-surface" />

                {(() => {
                    const dx = margin.left;
                    let polygonPoints = `0,0`;
                    let pathPoints = "";
                    for (const [roll, prob] of baselineData) {
                        const x = dx + probScale(prob);
                        const y = (
                            roll === 0 ? 0
                                : (roll === rollProbs.length ? height
                                        : margin.top + calcRollY(roll))
                        );
                        polygonPoints += ` ${x},${y}`;

                        if (pathPoints === "") {
                            pathPoints += "M";
                        } else {
                            pathPoints += "L";
                        }
                        pathPoints += ` ${x},${y}`;
                    }
                    polygonPoints += ` 0,${height}`;
                    return (
                        <polygon
                            points={polygonPoints}
                            className="fill-border-blue" />
                    );
                })()}

                <Group left={margin.left} top={margin.top}>
                    <AxisLeft
                        left={0}
                        tickFormat={roll => `${roll}`}
                        scale={rollScale}
                        hideAxisLine={true}
                        hideTicks={true}
                        tickLabelProps={{
                            className: "align-middle text-2xl font-bold fill-text-strong",
                            textAnchor: "end",
                            dy: "0.65em",
                            dx: "-0.1em",
                        }} />

                    <BarGroupHorizontal
                        data={data}
                        keys={keys}
                        width={renderXMax}
                        y0={group => group.roll}
                        y0Scale={rollScale}
                        y1Scale={groupScale}
                        xScale={probScale}
                        color={colourScale}>

                        {groups => groups.map(group => (
                            <Group key={`bar-group-${group.index}-${group.y0}`} top={group.y0}>
                                {group.bars.map(bar => (
                                    <RightRoundedRect
                                        key={`bar-group-bar-${group.index}-${bar.index}-${bar.value}-${bar.key}`}
                                        x={bar.x}
                                        y={bar.y}
                                        width={Math.max(2, bar.width)}
                                        height={bar.height}
                                        radius={Math.min(8, bar.width)}
                                        style={{ fill: bar.color }}
                                        strokeWidth={2} />
                                ))}
                            </Group>
                        ))}
                    </BarGroupHorizontal>
                </Group>
            </svg>
        </>
    );
}


interface RollDistributionGraphProps {
    className?: string;
    graphClassName?: string;
}


export function RollDistributionGraph({
    className, graphClassName, ...props
}: RollDistributionGraphProps & Omit<RollDistributionVisProps, "width" | "height">) {
    return (
        <figure className={className}>
            <div className="h-[24rem] w-full select-none [&_svg]:overflow-hidden [&_svg]:rounded-2xl">
                <ParentSize>
                    {({ width, height }) => (
                        <RollDistributionVis
                            className={graphClassName}
                            width={Math.min(width, height * 1.5)}
                            height={height}
                            {...props} />
                    )}
                </ParentSize>
            </div>
        </figure>
    );
}
